import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import {
  Box, Typography, Paper, Container, Button,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Checkbox, Chip, Avatar, CircularProgress,
  Fade, Stack, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import {
  ReportProblem as ReportProblemIcon,
  DeleteSweep as DeleteSweepIcon,
  Person as PersonIcon,
  Event as EventIcon,
  ErrorOutline as ErrorOutlineIcon,
  SupervisorAccount as SupervisorAccountIcon,
  School as SchoolIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { getAllComplains, deleteComplains } from '../../../redux/complainRelated/complainHandle';
import { useToast } from '../../../context/ToastContext';
import { formatDatePart } from '../../../utils/customDate';

// Animations
const fadeIn = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

// Styled Components
const PageWrapper = styled(Box)`
  background-color: #f8fafc;
  min-height: 100vh;
  padding-bottom: 80px;
  animation: ${fadeIn} 0.5s ease-out;
`;

const PremiumHeader = styled(Box)`
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  padding: 60px 0 40px;
  color: white;
  position: relative;
  overflow: hidden;
  
  &::after {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      width: 400px;
      height: 400px;
      background: radial-gradient(circle, rgba(239, 68, 68, 0.15) 0%, transparent 70%);
      border-radius: 50%;
      transform: translate(30%, -30%);
  }
`;

const ContentContainer = styled(Container)`
  margin-top: -80px;
  position: relative;
  z-index: 2;
`;

const PremiumCard = styled(Paper)`
  border-radius: 24px !important;
  border: 1px solid rgba(255, 255, 255, 0.8) !important;
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(20px) !important;
  padding: 32px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.05) !important;
  margin-bottom: 24px;
`;

const TableWrapper = styled(Paper)`
  border-radius: 24px !important;
  overflow: hidden;
  border: 1px solid #e2e8f0 !important;
  background: white !important;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.05) !important;
`;

const ActionBtn = styled(Button)`
  border-radius: 14px !important;
  padding: 10px 24px !important;
  font-weight: 800 !important;
  text-transform: none !important;
  box-shadow: 0 8px 16px rgba(239, 68, 68, 0.2) !important;
`;

const ComplainRow = styled(TableRow)`
  &:hover {
      background-color: #f8fafc;
  }
  transition: background-color 0.2s ease;
`;

const SeeComplains = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { complainsList, loading, error, response } = useSelector((state) => state.complain);
  const { currentUser } = useSelector(state => state.user);
  const { showToast } = useToast();

  const [selected, setSelected] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState(false);

  useEffect(() => {
    dispatch(getAllComplains(currentUser._id, "Complain"));
  }, [currentUser._id, dispatch]);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = complainsList.map((n) => n._id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;
  const numSelected = selected.length;

  const handleDeleteClick = () => {
    if (numSelected > 0) {
      setDeleteDialog(true);
    }
  };

  const confirmDelete = () => {
    dispatch(deleteComplains(currentUser._id, selected));
    setSelected([]);
    setDeleteDialog(false);
    showToast(`${numSelected} complain(s) deleted successfully`, "success");
    // Reload will trigger automatically or manually fetch again if needed
    setTimeout(() => {
      dispatch(getAllComplains(currentUser._id, "Complain"));
    }, 500);
  };

  const getUserTypeIcon = (userType) => {
    const type = userType.toLowerCase();
    if (type.includes('student')) return <SchoolIcon fontSize="small" />;
    if (type.includes('teacher')) return <SupervisorAccountIcon fontSize="small" />;
    return <PersonIcon fontSize="small" />;
  };

  const getUserTypeColor = (userType) => {
    const type = userType.toLowerCase();
    if (type.includes('student')) return '#3b82f6';
    if (type.includes('teacher')) return '#10b981';
    return '#6366f1';
  };

  return (
    <PageWrapper>
      <PremiumHeader>
        <Container maxWidth="xl">
          <Fade in timeout={800}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <IconButton onClick={() => navigate('/Admin/dashboard')} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant="h2" sx={{ fontWeight: 900, letterSpacing: '-0.03em', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <ReportProblemIcon sx={{ fontSize: 48, color: '#ef4444' }} />
                    User Complains
                  </Typography>
                </Box>
                <Typography variant="h6" sx={{ opacity: 0.7, fontWeight: 400, ml: 8 }}>
                  Review and manage complaints submitted by students and staff.
                </Typography>
              </Box>
            </Box>
          </Fade>
        </Container>
      </PremiumHeader>

      <ContentContainer maxWidth="xl" sx={{ mt: 5 }} >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={60} thickness={4} />
          </Box>
        ) : response || (complainsList && complainsList.length === 0) ? (
          <PremiumCard sx={{ p: 8, textAlign: 'center' }}>
            <ErrorOutlineIcon sx={{ fontSize: 80, color: '#cbd5e1', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#475569', mb: 1 }}>
              No Complains Found
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b' }}>
              Everything looks good! There are no complaints to review at this time.
            </Typography>
          </PremiumCard>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#64748b' }}>
                {numSelected} items selected
              </Typography>
              <Fade in={numSelected > 0}>
                <ActionBtn 
                  variant="contained" 
                  color="error" 
                  startIcon={<DeleteSweepIcon />}
                  onClick={handleDeleteClick}
                  sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                >
                  Delete Selected
                </ActionBtn>
              </Fade>
            </Box>

            <TableWrapper>
              <TableContainer>
                <Table sx={{ minWidth: 750 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell padding="checkbox" sx={{ py: 3 }}>
                        <Checkbox
                          color="primary"
                          indeterminate={numSelected > 0 && numSelected < complainsList.length}
                          checked={complainsList.length > 0 && numSelected === complainsList.length}
                          onChange={handleSelectAllClick}
                          sx={{ '&.Mui-checked': { color: '#ef4444' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, color: '#475569', width: '20%' }}>User Profile</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: '#475569', width: '60%' }}>Complaint Details</TableCell>
                      <TableCell sx={{ fontWeight: 800, color: '#475569', width: '20%' }}>Submitted On</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {complainsList && complainsList.map((complain, index) => {
                      const isItemSelected = isSelected(complain._id);
                      
                      let userType = 'Unknown';
                      if (complain.userModel) {
                          userType = complain.userModel.charAt(0).toUpperCase() + complain.userModel.slice(1);
                      } else if (complain.user && complain.user.role) {
                          userType = complain.user.role.charAt(0).toUpperCase() + complain.user.role.slice(1);
                      } else if (complain.user && complain.user._id) {
                          userType = 'User';
                      }

                      const dateString = complain.date ? formatDatePart(complain.date) : "No Date";

                      return (
                        <ComplainRow
                          hover
                          onClick={(event) => handleClick(event, complain._id)}
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={complain._id}
                          selected={isItemSelected}
                          sx={{ cursor: 'pointer', '&.Mui-selected': { bgcolor: '#fef2f2 !important' } }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              checked={isItemSelected}
                              sx={{ '&.Mui-checked': { color: '#ef4444' } }}
                            />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={2} alignItems="center">
                              <Avatar sx={{ 
                                  bgcolor: `${getUserTypeColor(userType)}15`, 
                                  color: getUserTypeColor(userType),
                                  width: 40, height: 40,
                                  borderRadius: '12px'
                              }}>
                                {getUserTypeIcon(userType)}
                              </Avatar>
                              <Typography sx={{ fontWeight: 700, color: '#1e293b' }}>
                                {userType}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ color: '#475569', lineHeight: 1.6 }}>
                              {complain.complaint}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                                icon={<EventIcon sx={{ fontSize: '1rem !important' }} />} 
                                label={dateString} 
                                size="small" 
                                sx={{ 
                                    bgcolor: '#f1f5f9', 
                                    color: '#64748b', 
                                    fontWeight: 600,
                                    borderRadius: '8px'
                                }} 
                            />
                          </TableCell>
                        </ComplainRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </TableWrapper>
          </>
        )}
      </ContentContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog
          open={deleteDialog}
          onClose={() => setDeleteDialog(false)}
          PaperProps={{
              sx: { borderRadius: '24px', p: 2, minWidth: 400 }
          }}
      >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 900, color: '#1e293b' }}>
              <ErrorOutlineIcon color="error" /> Confirm Deletion
          </DialogTitle>
          <DialogContent>
              <DialogContentText sx={{ color: '#64748b', fontWeight: 500, fontSize: '1.1rem' }}>
                  Are you sure you want to delete the {numSelected} selected complaint(s)? This action cannot be undone.
              </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
              <Button 
                  onClick={() => setDeleteDialog(false)} 
                  variant="outlined"
                  sx={{ borderRadius: '12px', fontWeight: 700, color: '#64748b', borderColor: '#cbd5e1' }}
              >
                  Cancel
              </Button>
              <Button 
                  onClick={confirmDelete} 
                  variant="contained" 
                  color="error"
                  sx={{ borderRadius: '12px', fontWeight: 700 }}
                  disableElevation
              >
                  Delete Selected
              </Button>
          </DialogActions>
      </Dialog>
    </PageWrapper>
  );
};

export default SeeComplains;
